import React, { useEffect, useMemo, useState } from "react";
import i18n from "utils/i18n";
import ThemedBlock, { HeaderRow } from "components/atoms/ThemedBlock";
import ObjectTimeslot from 'utils/objects/timeslot'
import HelperValidate from 'utils/helper-validate'
import { FormFieldsConstants, FormStatus } from 'utils/constants/form-constants'
import HelperString from 'utils/helper-string'
import Form from 'components/molecules/Form'
import _ from 'lodash'
import HelperDate, { dateFormat } from 'utils/helper-date'
import { useSelector } from 'react-redux'
import { dataConstants } from 'services/redux/data/constants'
import GraphqlBuilder from 'utils/request/graphql/builder'
import MutationTimeslotException from 'graphql/mutations/MutationTimeslotException'
import { StyleSheet, View } from 'react-native'
import Button from "theme/Button";
import Text from "theme/Text";
import { Column, Container, Grid, Row } from 'components/atoms/BootstrapGrid'
import { constantsActive } from 'utils/constants/constants'
import Tag, { tagAppearances } from 'components/atoms/Tag'
import VStack from 'components/atoms/stack/VStack'
import { ThemedValues } from 'styles/themed'
import { groupTimeslotExceptions } from 'utils/helper-timeslot-exceptions'

const KINDS = {
	BLOCK: 'block',
	REDUCE: 'reduce',
	INCREASE: 'increase',
	UNBLOCK: 'unblock',
	HIDDEN: 'hidden'
}

const ManagementBuildingsComponentsTimeslotExceptionsScene = ({ building, floor, room, readonly, onSubmit }) => {
	const [ isSaving, setSaving ] = useState( false );
	const _timeslotKinds = useSelector( state => {
		return state.data[dataConstants.TIMESLOT_KINDS];
	} ); // Keep for updates
	
	const [ formKey, setFormKey ] = useState( HelperString.random() );
	const [ selectTimeSlots, setSelectTimeSlotKinds ] = useState( [] );
	const [ formValues, setFormValues ] = useState( {
		kind: KINDS.BLOCK,
		dates: {
			startDate: new Date(),
			endDate: new Date()
		},
	} );
	
	useEffect( () => {
		let modeTimeslots;
		
		switch( true ) {
			case HelperValidate.isValid( room ):
				modeTimeslots = ObjectTimeslot.parse( room );
				break;
			
			case HelperValidate.isValid( floor ):
				modeTimeslots = ObjectTimeslot.parse( floor );
				break;
			
			default:
				modeTimeslots = ObjectTimeslot.parse( building );
				break;
		}
		
		modeTimeslots = _.sortBy( modeTimeslots, [ 'timeslot_day', 'timeslot_start' ] );
		
		let tmpTimeslots = [];
		_.forEach( modeTimeslots, ( modeTimeslot ) => {
			let timeslotKindLabel = '';
			_.forEach( _timeslotKinds, ( _timeslotKind ) => {
				if( _timeslotKind.kind === modeTimeslot.kind && _timeslotKind.sub_kind === modeTimeslot.sub_kind ) {
					timeslotKindLabel = _timeslotKind.name;
				}
			});
			
			tmpTimeslots.push({
				value: modeTimeslot.guid,
				label: timeslotKindLabel + ': ' +HelperDate.date().day( modeTimeslot.timeslot_day ).format('dd')+ ' [' + HelperDate.date( modeTimeslot.timeslot_start, "HH:mm:ss").format( 'HH:mm' ) + ' - ' + HelperDate.date( modeTimeslot.timeslot_end, "HH:mm:ss").format( 'HH:mm' ) + ']'
			})
		})
		
		setSelectTimeSlotKinds( tmpTimeslots )
	}, [ building, floor, room, _timeslotKinds ] );
	
	
	const updateStatus = ( exception, status ) => {
		
		let mutation  = new GraphqlBuilder()
			.setOnStart( setSaving.bind( this, true ) )
			.setOnError( setSaving.bind( this, false ) )
			.setOnSuccess( result => {
				if( onSubmit ){
					onSubmit();
				}
			});
		
		_.forEach( exception.exceptions, ( exception ) => {
			const variables = {
				guid: exception.guid,
				active: status
			}
			mutation.addQuery( 'mutation_timeslot_exception', MutationTimeslotException, variables )
		});
		
		mutation.execute();
		
	}
	const saveForm = ( values, { callback } = {}) => {
		// generate a guid for the group
		const group_identifier = HelperString.UUID();
		
		let mutation  = new GraphqlBuilder()
			.setOnStart( () => {
				callback( FormStatus.LOADING )
				setSaving( true )
			} )
			.setOnError( () => {
				callback( FormStatus.ERROR )
				setSaving( false )
			} )
			.setOnSuccess( result => {
				callback( FormStatus.SUCCESS )
				setFormKey( HelperString.random() );
				setFormValues( {
					kind: KINDS.BLOCK,
					dates: {
						startDate: new Date(),
						endDate: new Date()
					},
					timeslots: [],
					comment: '',
					reduce_by: '',
					increase_by: '',
				});
				
				if( onSubmit ){
					onSubmit();
				}
			})
		
		_.forEach( formValues.timeslots, ( timeslot ) => {
			const variables = {
				group_identifier: group_identifier,
				name: formValues.name,
				timeslot_guid: timeslot,
				kind: formValues.kind,
				date_start: HelperDate.date( formValues.dates[ 0 ] ).format( 'YYYY-MM-DD' ),
				date_end: HelperDate.date( formValues.dates[ 1 ] ).format( 'YYYY-MM-DD' ),
				reduce_by: _.parseInt( formValues.reduce_by ),
				increase_by: _.parseInt( formValues.increase_by ),
				comment: formValues.comment,
				active: 'yes'
			}
			
			mutation.addQuery( 'mutation_timeslot_exception', MutationTimeslotException, variables )
		});
		
		mutation.execute();
	}
	
	const formValid = useMemo( () => {
		let valid = true;

		if( formValues.dates[ 0 ] === null ){
			valid = false;
		}
		
		if( _.size( formValues.timeslots ) === 0 ){
			valid = false;
		}
		
		if( formValues.kind === KINDS.REDUCE && _.isNaN( _.parseInt(  formValues.reduce_by ) ) ){
			valid = false;
		}
		
		if( formValues.kind === KINDS.INCREASE && _.isNaN( _.parseInt(  formValues.increase_by ) ) ){
			valid = false;
		}
		
		return valid;
	}, [ formValues ] );
	
	const onFormChange = ( values ) => {
		setFormValues(  {..._.cloneDeep( values ), ...values } );
	}
	
	const kindOptions = [
		{ value: KINDS.BLOCK, label:  i18n.t( 'management:pages.buildings.timeslot_exceptions.kinds.block') },
		{ value: KINDS.REDUCE, label:  i18n.t( 'management:pages.buildings.timeslot_exceptions.kinds.reduce') },
		{ value: KINDS.INCREASE, label:  i18n.t( 'management:pages.buildings.timeslot_exceptions.kinds.increase') },
		{ value: KINDS.UNBLOCK, label: i18n.t( 'management:pages.buildings.timeslot_exceptions.kinds.unblock')},
		{ value: KINDS.HIDDEN, label: i18n.t( 'management:pages.buildings.timeslot_exceptions.kinds.hidden')},
	];
	
	let formFields = [
		{
			label: i18n.t('management:pages.buildings.timeslot_exceptions.name'),
			name: 'name',
			type: FormFieldsConstants.TEXT,
		},
		{
			label: i18n.t('management:pages.buildings.timeslot_exceptions.kind'),
			name: 'kind',
			type: FormFieldsConstants.SELECT,
			placeholder: i18n.t( 'management:ui.choose' ),
			options: kindOptions
		}
		
	];
	
	formFields.push({
		label: i18n.t('management:pages.buildings.timeslot_exceptions.date_start_end'),
		name: 'dates',
		type: FormFieldsConstants.DATE_RANGED_PICKER,
		range: formValues.dates
	});
	
	if( selectTimeSlots.length > 0){
		formFields.push({
			label: i18n.t('management:pages.buildings.timeslot_exceptions.timeslots'),
			name: 'timeslots',
			type: FormFieldsConstants.SELECT_MULTI,
			options: selectTimeSlots
		});
	}
	
	if( formValues?.kind === KINDS.REDUCE ) {
		formFields.push({
			label: i18n.t('management:pages.buildings.timeslot_exceptions.reduce_by'),
			name: 'reduce_by',
			type: FormFieldsConstants.NUMBER
		});
	}
	
	if( formValues?.kind === KINDS.INCREASE ) {
		formFields.push({
			label: i18n.t('management:pages.buildings.timeslot_exceptions.increase_by'),
			name: 'increase_by',
			type: FormFieldsConstants.NUMBER
		});
	}
	
	if( formValues?.kind === KINDS.BLOCK ) {
		formFields.push( {
			label: i18n.t( 'management:pages.buildings.timeslot_exceptions.comment' ),
			name: 'comment',
			type: FormFieldsConstants.TEXT
		} );
	}
	
	const exceptions = useMemo( () => {
		let modeTimeslots;
		switch( true ) {
			case HelperValidate.isValid( room ):
				modeTimeslots = ObjectTimeslot.parse( room );
				break;
			
			case HelperValidate.isValid( floor ):
				modeTimeslots = ObjectTimeslot.parse( floor );
				break;
			
			default:
				modeTimeslots = ObjectTimeslot.parse( building );
		}
		
		const exceptions = groupTimeslotExceptions(modeTimeslots, _timeslotKinds);

		return _.map( exceptions, ( exception, key ) => {
			return (
				<ThemedBlock
					key={ HelperString.hash( 'exception' ) + exception.group_identifier }
					header={(
						<HeaderRow>
							<View style={staticStyles.viewWidth100FlexDirectionRow}>
								<View style={staticStyles.viewFlex4}>
									<Text category="h6">{ exception.name }</Text>
								</View>
								<View style={staticStyles.viewFlex1AlignItemsFlexEnd}>
									<Choose>
										<When condition={exception.active === constantsActive.YES}>
											<Tag appearance={tagAppearances.GREEN}>{i18n.t( 'management:generic.active.yes')}</Tag>
										</When>
										<Otherwise>
											<Tag appearance={tagAppearances.RED}>{i18n.t( 'management:generic.active.no')}</Tag>
										</Otherwise>
									</Choose>
								</View>
							</View>
						</HeaderRow>
					)}
					shadow
					mediumPadding>
					
					<View style={staticStyles.viewFlexDirectionRow}>
						<View style={staticStyles.viewFlex9}>
							<Grid>
								<Container>
									<Row>
										<Column span={{ xs: 3, md: 3 }}>
											<Text category="s1">{ i18n.t( 'management:pages.buildings.timeslot_exceptions.kind' )}</Text>
										</Column>
										<Column span={{ xs: 9, md: 9 }}>
											<Text>{ i18n.t( `management:pages.buildings.timeslot_exceptions.kinds.${exception.kind}` ) }</Text>
										</Column>
									</Row>
									
									<Row>
										<Column span={{ xs: 3, md: 3 }}>
											<Text category="s1">{ i18n.t( 'management:pages.buildings.timeslot_exceptions.date_start_end' )}</Text>
										</Column>
										<Column span={{ xs: 9, md: 9 }}>
											<Text>{ HelperDate.date( exception.date_start ).format( dateFormat.DATE_PRETTY ) } / { HelperDate.date( exception.date_end ).format( dateFormat.DATE_PRETTY ) }</Text>
										</Column>
									</Row>
									
									{ exception.comment && (
										<Row>
											<Column span={{ xs: 3, md: 3 }}>
												<Text category="s1">{ i18n.t( 'management:pages.buildings.timeslot_exceptions.comment' )}</Text>
											</Column>
											<Column span={{ xs: 9, md: 9 }}>
												<Text>{ exception.comment }</Text>
											</Column>
										</Row>
									)}
									
									<Row>
										<Column span={{ xs: 3, md: 3 }}>
											<Text category="s1">{ i18n.t( 'management:pages.buildings.timeslot_exceptions.timeslots' )}</Text>
										</Column>
										<Column span={{ xs: 9, md: 9 }}>
											{ _.map( exception.timeslots, ( timeslot ) => {
												return (
													<View key={ `timeslot_row_${timeslot.guid}`}>
														<Text>{ (timeslot.kind_record?.name ?? timeslot.sub_kind ?? timeslot.kind ?? '') + ' - ' +HelperDate.date().day( timeslot.timeslot_day ).format('dd')+ ' - ' + HelperDate.date( timeslot.timeslot_start, "HH:mm:ss").format( 'HH:mm' ) + ' - ' + HelperDate.date( timeslot.timeslot_end, "HH:mm:ss").format( 'HH:mm' ) + ''  }</Text>
													</View>
												);
											})}
										</Column>
									</Row>
								</Container>
							</Grid>
						</View>
						
						<View style={staticStyles.viewFlex2}>
							{ exception.active === 'yes' && (
								<Button size="small" status="danger" onPress={ updateStatus.bind( this, exception, 'no')}  >
									{ i18n.t('management:generic.deactivate') }
								</Button>
							)}
							
							{ ( exception.active === 'no' && exception.date_end >  HelperDate.date().format( dateFormat.DATE ) )  && (
								<Button size="small" onPress={ updateStatus.bind( this, exception, 'yes')}  >
									{ i18n.t('management:generic.activate') }
								</Button>
							)}
						</View>
					</View>
				</ThemedBlock>
			);
		});
	}, [ building, floor, room, _timeslotKinds ] );
	
	return (
		<VStack
			space={ ThemedValues.stackSpace }>
			
			<ThemedBlock
				header={ i18n.t('management:form_block.title_timeslot_exceptions', { count: 1 } ) }
				shadow
				mediumPadding>
				
				<Form
					key={ HelperString.hash( 'form' ) + formKey }
					values={ formValues }
					onChange={ onFormChange }
					onSubmit={ saveForm }
					fields={ formFields }
					forceSaveButton={ formValid }
					isSubmitting={ isSaving }
				/>
			</ThemedBlock>
			
			{ exceptions }
		</VStack>
	);
}

const staticStyles = StyleSheet.create({
	viewWidth100FlexDirectionRow: {
		width: '100%',
		flexDirection: 'row',
		justifyContent: 'space-between',
	},
	viewFlex4: {
		flex: 4,
	},
	viewFlex1AlignItemsFlexEnd: {
		flex: 1,
		alignItems: 'flex-end',
	},
	viewFlexDirectionRow: {
		flexDirection: 'row',
	},
	viewFlex9: {
		flex: 9,
	},
	viewFlex2: {
		flex: 2,
	},
});

export default ManagementBuildingsComponentsTimeslotExceptionsScene
